import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublishIcon from '@mui/icons-material/Publish';
import ScoreIcon from '@mui/icons-material/Score';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Logger from "../../../../services/Logger";
import CustomSnackbar from "../../../components/CustomSnackbar";
import MiniPuzRow from "../../../pzl_common/MiniPuzRow";
import VerifyDialog from "../../../pzl_common/VerifyDialog";
import ResolvePuzzleDialog from "../../../pzl_common/ResolvePuzzleDialog";
import WordMeaning from "../../../pzl_common/WordMeaning";
import WordRedefine from "../../../pzl_common/WordRedefine";
import BorderHeader from "../../../pzl_common/BorderHeader";
import WordNetworkDialog from "../../../pzl_common/WordNetworkDialog";
import WordAlreadyPlacedDialog from "../../../pzl_common/WordAlreadyPlacedDialog";
import WordScoreDialog from "../../../pzl_common/WordScoreDialog";
import AdminCrossBoardService, { DRAFT, PUBLISHED, READY_TO_REVIEW, REVIEWED } from "../AdminCrossBoardService";
import ApiService from "../../../../services/ApiService";
import CreatorCrossService from "../../../../creator/service/CreatorCrossService";
import DatamuseService from "../../../../services/DatamuseService";
import UserService from "../../../../services/UserService";
import { usePuzzleData } from "../usePuzzleData";
import CrossCanAssoc from "./CrossCanAssoc";
import CrossCandManual from "./CrossCandManual";
import CrossCandAI from "./CrossCandAI";
import { generatePositions, getGridWord } from "./CrossCandUtils";
import { DIRECTION_HORIZONTAL } from "../../../pzl_common/PuzzleUtil";
import AddBannedWordDialog from "../../../setting/banned_words/AddBannedWordDialog";
import BannedService from "../../../setting/banned_words/BannedService";
import BannedWordsPopup from "../../../setting/banned_words/BannedWordsPopup";
import ValidationDialog from "../../../../creator/common/ValidationDialog";
import CrossFillDialog from "../CrossFillDialog";
import FillPatternTagsDialog from "./FillPatternTagsDialog";
import MatchKeywordDialog from "./MatchKeywordDialog";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

export default function CrossCandEdit() {
    const { crossId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [refresh, setRefresh] = useState(0);
    const { crossTpl, loading } = usePuzzleData(crossId, refresh);

    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedAssociatedWord, setSelectedAssociatedWord] = useState(null);
    const [selectedNetworkWord, setSelectedNetworkWord] = useState(null);
    const abortControllerRef = useRef(null);

    // Mode state - use ref to preserve across word additions
    const modeRef = useRef('assoc');
    const [, setMode] = useState(() => modeRef.current);

    const [selectedManualWord, setSelectedManualWord] = useState(null);

    // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Discard confirmation dialog state
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);

    // Word to delete state
    const [wordToDelete, setWordToDelete] = useState(null);

    // Puzzle type selection dialog state
    const [puzzleTypeDialogOpen, setPuzzleTypeDialogOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [resolveDialogType, setResolveDialogType] = useState("duplicates");

    // State transition dialogs
    const [draftDialogOpen, setDraftDialogOpen] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(false);
    const [readyToReviewDialogOpen, setReadyToReviewDialogOpen] = useState(false);
    const [loadingReadyToReview, setLoadingReadyToReview] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [loadingPublish, setLoadingPublish] = useState(false);
    const [backToReviewedDialogOpen, setBackToReviewedDialogOpen] = useState(false);
    const [loadingBackToReviewed, setLoadingBackToReviewed] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [isManager, setIsManager] = useState(false);
    const [reviewedDialogOpen, setReviewedDialogOpen] = useState(false);
    const [loadingReviewed, setLoadingReviewed] = useState(false);

    // Title hook loading state
    const [loadingApplyTitleHook, setLoadingApplyTitleHook] = useState(false);
    const [showTitleHookDialog, setShowTitleHookDialog] = useState(false);
    // Fill dialog (single popup with Fill Assoc, Candidate Words, Match Keyword)
    const [fillDialogOpen, setFillDialogOpen] = useState(false);
    const [loadingFillPatternAdd, setLoadingFillPatternAdd] = useState(false);
    const [loadingMatchKeywordAdd, setLoadingMatchKeywordAdd] = useState(false);

    // WordRedefine dialog state
    const [openWordRedefineDialog, setOpenWordRedefineDialog] = useState(false);
    const [editingDefinition, setEditingDefinition] = useState("");
    const [editingWord, setEditingWord] = useState("");

    // Banned words popup state
    const [openBannedWordsPopup, setOpenBannedWordsPopup] = useState(false);
    const [openAddBannedWordDialog, setOpenAddBannedWordDialog] = useState(false);
    const [selectedWordForBanned, setSelectedWordForBanned] = useState("");
    const [loadingAddBanned, setLoadingAddBanned] = useState(false);

    // Word Network dialog state
    const [networkDialogOpen, setNetworkDialogOpen] = useState(false);

    // Word already placed dialog state (AI Associations)
    const [alreadyPlacedDialogOpen, setAlreadyPlacedDialogOpen] = useState(false);
    const [alreadyPlacedWord, setAlreadyPlacedWord] = useState("");

    // Word Score dialog state
    const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
    const [wordScores, setWordScores] = useState([]);
    const [loadingScores, setLoadingScores] = useState(false);
    const [loadingRearrange, setLoadingRearrange] = useState(false);

    // Square state - initialize from filledCoordinates
    const [square, setSquare] = useState([]);

    // Memoized parsed filledCoordinates
    const filledCoordinates = useMemo(() => {
        if (!crossTpl?.filledCoordinates) {
            return [];
        }
        try {
            const parsed = JSON.parse(crossTpl.filledCoordinates);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            Logger.error("Error parsing filledCoordinates:", error, crossTpl.filledCoordinates);
            return [];
        }
    }, [crossTpl?.filledCoordinates]);

    // Initialize square from filledCoordinates
    useEffect(() => {
        if (!loading && crossTpl) {
            Logger.log("CrossCandEdit: Initializing square", {
                hasFilledCoordinates: !!crossTpl.filledCoordinates,
                filledCoordinatesLength: filledCoordinates.length,
                filledCoordinates: filledCoordinates
            });

            if (filledCoordinates.length > 0) {
                const newSquare = initializeSquare(filledCoordinates);
                Logger.log("CrossCandEdit: Initialized square with data", {
                    squareSize: newSquare.length,
                    firstRow: newSquare[0],
                    hasData: newSquare.some(row => row.some(cell => cell !== "" && cell !== 0))
                });
                setSquare(newSquare);
            } else {
                // Even if filledCoordinates is empty, initialize an empty 12x12 grid
                const newSquare = initializeSquare([]);
                Logger.log("CrossCandEdit: Initialized empty square", {
                    squareSize: newSquare.length
                });
                setSquare(newSquare);
            }
        }
    }, [filledCoordinates, crossTpl, loading]);

    // Function to initialize the square array
    const initializeSquare = (filledCoordinates) => {
        const size = 12;
        let newSquare = Array.from({ length: size }, () => Array(size).fill(0));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        let found = "";

        if (filledCoordinates && Array.isArray(filledCoordinates)) {
            // Filter out invalid entries and log structure for debugging
            const validEntries = filledCoordinates.filter((c) => {
                const isValid = c && c.positions && Array.isArray(c.positions);
                if (!isValid && c) {
                    Logger.warn("CrossCandEdit: Invalid filledCoordinates entry:", c);
                }
                return isValid;
            });

            found = validEntries
                .map((c) => c.positions)
                .map((positions) => {
                    const matchingPosition = positions.find((p) => p && p.row === row && p.col === col);

                    if (matchingPosition && matchingPosition.value) {
                        // Normalize French accents (remove diacritical marks) before converting to uppercase
                        const char = matchingPosition.value;
                        const normalized = char
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, ''); // Remove diacritical marks
                        return normalized.toUpperCase();
                    }
                    return "";
                })
                .find((value) => value !== "");
        }

        return found || "";
    };

    // Check if puzzle has word network data
    const hasWordNetwork = useMemo(() => {
        const wordNetwork = crossTpl?.template?.wordNetwork || crossTpl?.wordNetwork;
        return wordNetwork?.nodes && wordNetwork?.links;
    }, [crossTpl]);

    // Get word network data
    const wordNetworkData = useMemo(() => {
        return crossTpl?.template?.wordNetwork || crossTpl?.wordNetwork;
    }, [crossTpl]);

    // Calculate puzzle statistics
    const puzzleStats = useMemo(() => {
        const totalCells = 12 * 12; // 12x12 grid
        let filledCells = 0;
        const cellSet = new Set();

        // Count filled cells and check for duplicates
        for (let row = 0; row < square.length; row++) {
            for (let col = 0; col < square[row]?.length; col++) {
                const cellValue = square[row][col];
                if (cellValue && cellValue !== "" && cellValue !== "0") {
                    filledCells++;
                    const cellKey = `${row},${col}`;
                    cellSet.add(cellKey);
                }
            }
        }

        // Check for duplicate words
        const wordTexts = filledCoordinates.map(coord => coord.text?.toLowerCase()).filter(Boolean);
        const uniqueWords = new Set(wordTexts);
        const hasDuplicates = wordTexts.length !== uniqueWords.size;

        const percentage = totalCells > 0 ? (filledCells / totalCells) * 100 : 0;
        const thresholdPercentage = parseInt(process.env.REACT_APP_CROSSWORD_THRESHOLD_PERCENTAGE || "85", 10);
        const thresholdCells = Math.ceil((thresholdPercentage / 100) * totalCells);
        const thresholdMet = percentage >= thresholdPercentage;
        const cellsNeeded = thresholdMet ? 0 : Math.max(0, thresholdCells - filledCells);

        return {
            filledCells,
            totalCells,
            percentage: percentage.toFixed(1),
            hasDuplicates,
            thresholdMet,
            thresholdCells,
            thresholdPercentage,
            cellsNeeded
        };
    }, [square, filledCoordinates]);

    // Sync mode state with ref when crossTpl updates
    useEffect(() => {
        setMode(modeRef.current);
    }, [crossTpl?.id]);

    useEffect(() => {
        let cancelled = false;
        UserService.getUserInfo().then((u) => {
            const role = u?.cloudUser?.userRole;
            if (!cancelled && role && String(role).toUpperCase() === "MANAGER") {
                setIsManager(true);
            }
        }).catch(() => { });
        return () => { cancelled = true; };
    }, []);

    // Check if we're in creator context
    const isCreatorContext = location.pathname.startsWith('/creator');

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleWordSelect = async (candidate) => {
        // Validate candidate object
        if (!candidate) {
            Logger.error("handleWordSelect called with null/undefined candidate");
            return;
        }

        // Clear network word selection when selecting from other sources
        setSelectedNetworkWord(null);

        // Normalize candidate to ensure it has word property and positions
        const normalizedCandidate = {
            ...candidate,
            word: candidate.word || candidate.text || "",
            // Ensure positions array exists
            positions: candidate.positions && Array.isArray(candidate.positions) ? candidate.positions : []
        };

        // Check if this is an associated word (has specific properties that distinguish it from auto candidates)
        // Associated words from CrossCanAssoc have positions array and word property
        // They also typically have additional properties like startRow, startCol, direction, pattern, etc.
        if (normalizedCandidate.positions && normalizedCandidate.positions.length > 0 && normalizedCandidate.word &&
            (normalizedCandidate.startRow !== undefined || normalizedCandidate.startCol !== undefined || normalizedCandidate.direction || normalizedCandidate.pattern)) {
            // This is an associated word from the association mode
            setSelectedAssociatedWord(normalizedCandidate);
            setSelectedWord(null);
        } else {
            // This is a regular word from manual mode
            setSelectedWord(normalizedCandidate);
            setSelectedAssociatedWord(null);
        }
    };

    const handleWordAdd = async (candidate) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!crossTpl?.filledCoordinates) {
                handleSnackbar("No puzzle data available", "error");
                return;
            }

            // Validate candidate object
            if (!candidate) {
                handleSnackbar("Invalid word candidate", "error");
                return;
            }

            // Use candidate.gridWord if available (word without hyphens), otherwise candidate.word or candidate.text
            let wordText = candidate.gridWord || candidate.word || candidate.text;
            if (!wordText) {
                handleSnackbar("Word candidate has no text content", "error");
                return;
            }

            // Remove hyphens and spaces for grid placement (e.g. "feu de camp" -> "feudecamp")
            wordText = getGridWord(wordText);

            // Normalize French words by removing accents
            const language = crossTpl?.language || 'english';
            if (language === 'french') {
                wordText = normalizeFrenchWord(wordText);
            } else {
                wordText = wordText.toLowerCase();
            }

            // Get the isReversed flag from candidate
            const isReversed = candidate.isReversed || false;

            const filledCoordinates = JSON.parse(crossTpl.filledCoordinates);

            // Regenerate positions with the normalized word to ensure they match exactly
            let normalizedPositions = generatePositions(
                candidate.startRow ?? candidate.positions[0]?.row ?? 0,
                candidate.startCol ?? candidate.positions[0]?.col ?? 0,
                candidate.direction,
                wordText.length,
                wordText,
                isReversed
            );

            // When isReversed is true, reverse the character values in positions to match the reversed word order
            if (isReversed && wordText) {
                normalizedPositions = normalizedPositions.map((pos, index) => ({
                    ...pos,
                    value: wordText[wordText.length - 1 - index] || pos.value
                }));
            }

            // Create new word entry
            const newWordEntry = {
                id: uuidv4(),
                text: wordText,
                direction: candidate.direction,
                length: wordText.length,
                reversed: isReversed,
                positions: normalizedPositions
            };

            // Add to filledCoordinates
            const updatedCoordinates = [...filledCoordinates, newWordEntry];

            // Update puzzle via API
            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedCoordinates, wordText);

            handleSnackbar(`Word "${wordText}" added successfully!`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);

        } catch (error) {
            Logger.error("Error adding new word:", error);
            handleSnackbar("Error adding new word", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleBack = () => {
        // Check if we're in creator context (path starts with /creator)
        if (location.pathname.startsWith('/creator')) {
            navigate('/creator/crossword');
        } else {
            navigate(`/admin/puzzle/cross_board/draft/${crossId}`);
        }
    };

    const handleModeChange = (_event, newMode) => {
        if (newMode !== null) {
            // Update ref immediately to persist across re-renders
            modeRef.current = newMode;
            setMode(newMode);
            // Clear selections when switching modes
            setSelectedWord(null);
            setSelectedAssociatedWord(null);
            setSelectedManualWord(null);
            setSelectedNetworkWord(null);
        }
    };

    const handleManualWordSelect = async (candidate) => {
        // Clear network word selection when selecting manual word
        setSelectedNetworkWord(null);
        setSelectedManualWord(candidate);
    };

    /** Returns true if the word is already placed in the grid (and shows the dialog); false otherwise. */
    const checkWordAlreadyPlaced = (candidate) => {
        if (!candidate) return false;
        let wordText = candidate.gridWord || candidate.word || candidate.text;
        if (!wordText) return false;
        wordText = getGridWord(wordText);
        const language = crossTpl?.language || 'english';
        if (language === 'french') {
            wordText = normalizeFrenchWord(wordText);
        } else {
            wordText = wordText.toLowerCase();
        }
        const existingFilled = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
        const displayWord = candidate.word || wordText;
        const alreadyPlaced = existingFilled.some((entry) => {
            const entryText = entry.text ? (language === 'french' ? normalizeFrenchWord(entry.text) : entry.text.toLowerCase()) : '';
            return entryText === wordText;
        });
        if (alreadyPlaced) {
            setAlreadyPlacedWord(displayWord);
            setAlreadyPlacedDialogOpen(true);
            return true;
        }
        return false;
    };

    const handleManualWordAdd = async (candidate) => {
        try {
            // Validate candidate object
            if (!candidate) {
                handleSnackbar("Invalid manual word candidate", "error");
                return false;
            }

            // Use candidate.gridWord if available (word without hyphens), otherwise candidate.word or candidate.text
            // gridWord is the version used for grid placement, which is what we need to normalize
            let wordText = candidate.gridWord || candidate.word || candidate.text;
            if (!wordText) {
                handleSnackbar("Manual word candidate has no text content", "error");
                return false;
            }

            // Remove hyphens and spaces for grid placement (e.g. "feu de camp" -> "feudecamp")
            wordText = getGridWord(wordText);

            // Normalize French words by removing accents
            const language = crossTpl?.language || 'english';
            if (language === 'french') {
                wordText = normalizeFrenchWord(wordText);
            } else {
                wordText = wordText.toLowerCase();
            }

            // Check if this word is already placed in the puzzle grid
            const existingFilled = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
            const displayWord = candidate.word || wordText;
            const alreadyPlaced = existingFilled.some((entry) => {
                const entryText = entry.text ? (language === 'french' ? normalizeFrenchWord(entry.text) : entry.text.toLowerCase()) : '';
                return entryText === wordText;
            });
            if (alreadyPlaced) {
                setAlreadyPlacedWord(displayWord);
                setAlreadyPlacedDialogOpen(true);
                return false;
            }

            // Get the isReversed flag from candidate
            const isReversed = candidate.isReversed || false;

            // Create the new word entry
            // When isReversed is true, the positions array already has the correct values
            // in pos.value from generatePositions, so we should use those
            const newWordEntry = {
                id: uuidv4(),
                text: wordText,
                direction: candidate.direction,
                length: wordText.length,
                reversed: isReversed,
                positions: candidate.positions.map((pos, index) => {
                    // Always prefer pos.value if it's set (it's correct from generatePositions)
                    // This is especially important for reversed words where the character mapping is complex
                    if (pos.value !== undefined && pos.value !== "" && pos.value !== null) {
                        return {
                            ...pos,
                            value: pos.value.toLowerCase() // Ensure lowercase for consistency
                        };
                    }
                    // Fallback: map the word characters correctly
                    // When isReversed, we need to map: word[length-1-index] to position index
                    // When not reversed, we map: word[index] to position index
                    const charIndex = isReversed ? (wordText.length - 1 - index) : index;
                    const charValue = (charIndex >= 0 && charIndex < wordText.length)
                        ? wordText[charIndex]
                        : (pos.value || "");
                    return {
                        ...pos,
                        value: charValue.toLowerCase()
                    };
                })
            };

            // Add to filled coordinates
            const updatedFilledCoordinates = [...(crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : []), newWordEntry];

            // Update via API
            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, newWordEntry.text);

            handleSnackbar(`Word "${wordText}" added successfully!`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);

            // Reset manual selection
            setSelectedManualWord(null);

            return true;
        } catch (error) {
            Logger.error("Error adding manual word:", error);
            handleSnackbar("Error adding word. Please try again.", "error");
            return false;
        }
    };

    // Cell click handler for manual and AI mode
    const handleCellClick = (row, col) => {
        if ((modeRef.current === 'manual' || modeRef.current === 'ai')) {
            if (selectedManualWord) {
                // Update the selected manual word's position
                const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word || '');
                const updatedWord = {
                    ...selectedManualWord,
                    startRow: row,
                    startCol: col,
                    positions: generatePositions(row, col, selectedManualWord.direction, gridWord.length, gridWord, selectedManualWord.isReversed)
                };
                setSelectedManualWord(updatedWord);
            } else {
                // Even if no word is selected, update the position by creating a temporary word object
                // This will trigger the useEffect in CrossCandManual/CrossCandAI to update row/col
                const tempWord = {
                    startRow: row,
                    startCol: col,
                    direction: DIRECTION_HORIZONTAL,
                    isReversed: false
                };
                setSelectedManualWord(tempWord);
                // Clear it immediately after the useEffect processes it
                setTimeout(() => {
                    setSelectedManualWord(prev => {
                        // Only clear if it's still the temp word (has no word property)
                        if (prev && !prev.word) {
                            return null;
                        }
                        return prev;
                    });
                }, 100);
            }
        }
    };

    const handleDiscardPuzzle = async () => {
        try {
            if (isCreatorContext) {
                await CreatorCrossService.updateCrossState(crossTpl.id, "DISCARDED");
                navigate('/creator/crossword');
            } else {
                await AdminCrossBoardService.markAsDiscarded(crossTpl.id);
                navigate(`/admin/puzzle/cross_board/draft/${crossId}`);
            }
            handleSnackbar("Puzzle discarded successfully", "success");
        } catch (error) {
            Logger.error("Error discarding puzzle:", error);
            handleSnackbar("Error discarding puzzle", "error");
        }
    };

    const handleDisablePuzzle = async () => {
        try {
            if (isCreatorContext) {
                await CreatorCrossService.updateCrossState(crossTpl.id, "DISABLED");
            } else {
                await AdminCrossBoardService.updateCrossTplState(crossTpl.id, "DISABLED");
            }
            handleSnackbar("Puzzle disabled successfully", "success");
            setRefresh(prev => prev + 1);
        } catch (error) {
            Logger.error("Error disabling puzzle:", error);
            handleSnackbar("Error disabling puzzle", "error");
        }
    };

    const handleDeleteWordFromNetwork = async (wordText) => {
        if (!wordText) return;

        // Set the word to delete and show confirmation dialog
        setWordToDelete(wordText);
        setDiscardDialogOpen(true);
    };

    const handleConfirmDeleteWord = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!wordToDelete) return;

            // Get current filled coordinates and remove the word's positions
            const filledCoords = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
            const updatedCoords = filledCoords.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete?.toUpperCase()
            );

            // Call API to update the puzzle
            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedCoords, null);

            handleSnackbar(`Word "${wordToDelete}" removed successfully`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);
            setWordToDelete(null);
            setDiscardDialogOpen(false);

        } catch (error) {
            Logger.error("Error deleting word:", error);
            handleSnackbar("Error deleting word", "error");
            setDiscardDialogOpen(false);
            setWordToDelete(null);
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleApplyTitleHook = () => {
        setShowTitleHookDialog(true);
    };

    const confirmApplyTitleHook = async () => {
        if (!crossTpl?.id) return;

        try {
            setLoadingApplyTitleHook(true);
            const result = await AdminCrossBoardService.applyTitleHook(crossTpl.id);

            if (result.code && result.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to apply title hook", "error");
            } else {
                handleSnackbar("Title hook applied successfully", "success");
                setRefresh(prev => prev + 1);
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            handleSnackbar("Failed to apply title hook. Please try again.", "error");
        } finally {
            setLoadingApplyTitleHook(false);
        }
    };

    const handleRearrangeWords = async () => {
        setLoadingRearrange(true);
        try {
            await AdminCrossBoardService.rearrangeWords(crossTpl.id);
            handleSnackbar("Words rearranged successfully", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error rearranging words:", err);
            handleSnackbar(err.message || "Failed to rearrange words. Please try again.", "error");
        } finally {
            setLoadingRearrange(false);
        }
    };

    const handleFillPatternAdd = async (item) => {
        if (!crossTpl?.id || !item?.word) return;
        setLoadingFillPatternAdd(true);
        try {
            const displayWord = (item.word || "").trim(); // Keep original for display (e.g. "dry off")
            const gridWord = getGridWord(displayWord.toLowerCase()); // No spaces for grid (e.g. "dryoff")
            // Use positions from API return value (item.positions or item.coordinates?.positions)
            const rawPositions = item.coordinates?.positions ?? item.positions ?? [];
            const direction = inferDirectionFromPositions(rawPositions);

            // Use the actual returned positions, sorted, with values from gridWord (do not regenerate)
            const positions = rawPositions
                .filter((p) => (p.letter || p.value || "").trim() !== "" || (p.row != null && p.col != null))
                .sort((a, b) =>
                    direction === "horizontal" ? a.col - b.col : a.row - b.row
                )
                .slice(0, gridWord.length)
                .map((p, i) => ({
                    row: p.row,
                    col: p.col,
                    value: gridWord[i] || ""
                }));

            const newWordEntry = {
                id: uuidv4(),
                text: displayWord,
                direction,
                length: gridWord.length,
                reversed: false,
                positions
            };

            const existingFilled = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
            const updatedFilledCoordinates = [...existingFilled, newWordEntry];

            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, gridWord);

            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding fill pattern word:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingFillPatternAdd(false);
        }
    };

    const inferDirectionFromPositions = (positions) => {
        if (!Array.isArray(positions) || positions.length <= 1) return "horizontal";
        const sorted = [...positions].sort((a, b) => (a.row !== b.row ? a.row - b.row : a.col - b.col));
        const sameRow = sorted.every((p) => p.row === sorted[0].row);
        const sameCol = sorted.every((p) => p.col === sorted[0].col);
        if (sameRow) return "horizontal";
        if (sameCol) return "vertical";
        return "horizontal";
    };

    const handleMatchKeywordAdd = async (item) => {
        if (!crossTpl?.id || !item?.word) return;
        setLoadingMatchKeywordAdd(true);
        try {
            const displayWord = (item.word || "").trim();
            const gridWord = getGridWord(displayWord.toLowerCase());
            // Support both coordinates.positions (e.g. final-6 / fill-assoc API) and item.positions
            const rawPositions = item.coordinates?.positions ?? item.positions ?? [];
            if (rawPositions.length === 0) {
                handleSnackbar("No positions for this word; cannot place.", "error");
                return;
            }
            const direction = inferDirectionFromPositions(rawPositions);
            const startPos = rawPositions.reduce((a, b) =>
                direction === "horizontal" ? (a.col <= b.col ? a : b) : (a.row <= b.row ? a : b)
            );
            // Build positions with row, col, value so the grid preview can render the word
            const positions = generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false);

            const newWordEntry = {
                id: uuidv4(),
                text: displayWord,
                direction,
                length: gridWord.length,
                reversed: false,
                positions
            };

            const existingFilled = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
            const updatedFilledCoordinates = [...existingFilled, newWordEntry];

            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, gridWord);

            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding match keyword word:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingMatchKeywordAdd(false);
        }
    };

    /** Place all cart words in one batch (accumulated coordinates so each save includes previous words). */
    const handlePlaceWords = async (items) => {
        if (!crossTpl?.id || !Array.isArray(items) || items.length === 0) return;
        setLoadingMatchKeywordAdd(true);
        try {
            const existingFilled = crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
            let filledSoFar = [...existingFilled];
            const addedWords = [];
            let skippedNoPositions = 0;

            for (const item of items) {
                const displayWord = (item.word ?? item.text ?? "").toString().trim();
                if (!displayWord) continue;
                const gridWord = getGridWord(displayWord.toLowerCase());
                const rawPositions = item.coordinates?.positions ?? item.positions ?? [];
                if (rawPositions.length === 0) {
                    skippedNoPositions += 1;
                    continue;
                }
                const direction = inferDirectionFromPositions(rawPositions);
                const startPos = rawPositions.reduce((a, b) =>
                    direction === "horizontal" ? (a.col <= b.col ? a : b) : (a.row <= b.row ? a : b)
                );
                const positions = generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false);
                const newWordEntry = {
                    id: uuidv4(),
                    text: displayWord,
                    direction,
                    length: gridWord.length,
                    reversed: false,
                    positions
                };
                addedWords.push(newWordEntry);
                filledSoFar = [...filledSoFar, newWordEntry];
                await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, filledSoFar, gridWord);
            }

            if (addedWords.length === 0) {
                if (skippedNoPositions > 0) {
                    handleSnackbar(`${skippedNoPositions} word(s) skipped (no position data).`, "warning");
                }
                setLoadingMatchKeywordAdd(false);
                return;
            }
            const wordList = addedWords.map((e) => e.text).filter(Boolean).join(", ");
            const count = addedWords.length;
            const msg = count === 1 ? `"${wordList}" added successfully!` : `${count} words added: ${wordList}`;
            const skipMsg = skippedNoPositions > 0 ? ` ${skippedNoPositions} word(s) skipped (no position data).` : "";
            handleSnackbar(msg + skipMsg, skippedNoPositions > 0 ? "warning" : "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding words from cart:", err);
            handleSnackbar(err?.message ?? "Failed to add words. Please try again.", "error");
        } finally {
            setLoadingMatchKeywordAdd(false);
        }
    };

    const handleReadyForReview = () => {
        if (crossTpl?.state === "REVIEWED") {
            setReadyToReviewDialogOpen(true);
        } else {
            setPuzzleTypeDialogOpen(true);
        }
    };

    const confirmReadyToReview = async () => {
        if (!crossTpl?.id) return;
        try {
            setLoadingReadyToReview(true);
            const updateFn = isCreatorContext ? CreatorCrossService.updateCrossState : AdminCrossBoardService.updateCrossTplState;
            await updateFn(crossTpl.id, "READY_TO_REVIEW");
            handleSnackbar("Puzzle moved back to Ready to Review", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving puzzle to Ready to Review:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingReadyToReview(false);
            setReadyToReviewDialogOpen(false);
        }
    };

    const handleReviewedClick = () => {
        if (crossTpl?.state === PUBLISHED) setBackToReviewedDialogOpen(true);
        else setReviewedDialogOpen(true);
    };

    const confirmPublish = async () => {
        if (!crossTpl?.id) return;
        setPublishDialogOpen(false);
        setLoadingPublish(true);
        try {
            const wordList = crossTpl.filledCoordinates
                ? JSON.parse(crossTpl.filledCoordinates).map((item) => item.text).filter(Boolean)
                : [];
            const details = await DatamuseService.requestWordScores(wordList);
            const result = await AdminCrossBoardService.publishCrossTpl(crossTpl.id, "", details, crossTpl.free || false);
            if (result?.code === "FAILURE") {
                handleSnackbar(result?.message || "Publish failed", "error");
            } else {
                handleSnackbar("Puzzle published", "success");
                if (isCreatorContext) {
                    setRefresh((prev) => prev + 1);
                } else {
                    navigate(`/admin/puzzle/cross_board/published/${crossTpl.id}`);
                }
            }
        } catch (err) {
            Logger.error("Error publishing puzzle:", err);
            handleSnackbar(err?.message || "Failed to publish puzzle", "error");
        } finally {
            setLoadingPublish(false);
        }
    };

    const handleReviewedDialogClose = () => setReviewedDialogOpen(false);

    const handleDraftClick = () => setDraftDialogOpen(true);

    const confirmDraft = async () => {
        if (!crossTpl?.id) return;
        try {
            setLoadingDraft(true);
            const updateFn = isCreatorContext ? CreatorCrossService.updateCrossState : AdminCrossBoardService.updateCrossTplState;
            await updateFn(crossTpl.id, "DRAFT");
            handleSnackbar("Puzzle moved back to Draft", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving puzzle to Draft:", err);
            handleSnackbar(err?.message || "Failed to update state to Draft", "error");
        } finally {
            setLoadingDraft(false);
            setDraftDialogOpen(false);
        }
    };

    const confirmReviewed = async () => {
        if (!crossTpl?.id) return;
        try {
            setLoadingReviewed(true);
            const updateFn = isCreatorContext ? CreatorCrossService.updateCrossState : AdminCrossBoardService.updateCrossTplState;
            await updateFn(crossTpl.id, "REVIEWED");
            handleSnackbar("Puzzle marked as Reviewed", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error marking puzzle as Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state to Reviewed", "error");
        } finally {
            setLoadingReviewed(false);
            setReviewedDialogOpen(false);
        }
    };

    const handleStateBack = () => {
        const s = crossTpl?.state;
        if (s === READY_TO_REVIEW) handleDraftClick();
        else if (s === REVIEWED) setReadyToReviewDialogOpen(true);
        else if (s === PUBLISHED) setBackToReviewedDialogOpen(true);
    };

    const confirmBackToReviewed = async () => {
        if (!crossTpl?.id) return;
        setLoadingBackToReviewed(true);
        try {
            const updateFn = isCreatorContext ? CreatorCrossService.updateCrossState : AdminCrossBoardService.updateCrossTplState;
            const result = await updateFn(crossTpl.id, "REVIEWED");
            if (result?.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to move back to Reviewed", "error");
            } else {
                handleSnackbar("Puzzle moved back to Reviewed", "success");
                setRefresh((prev) => prev + 1);
            }
        } catch (err) {
            Logger.error("Error moving back to Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingBackToReviewed(false);
            setBackToReviewedDialogOpen(false);
        }
    };

    const handleStateForward = () => {
        const s = crossTpl?.state;
        if (s === DRAFT) handleReadyForReview();
        else if (s === READY_TO_REVIEW && isManager) handleReviewedClick();
    };

    const handlePuzzleTypeSelected = async (puzzleType) => {
        try {
            const result = await AdminCrossBoardService.updateCrossTplState(crossTpl.id, READY_TO_REVIEW, puzzleType);

            if (result && (result.code === "SUCCESS" || !result.code)) {
                handleSnackbar("Puzzle moved to review stage successfully", "success");
                if (isCreatorContext) {
                    navigate('/creator/crossword');
                } else {
                    navigate(`/admin/puzzle/cross_board/ready-to-review/${crossTpl.id}?fromStep=1`);
                }
            } else {
                handleSnackbar(result?.message || "Failed to update puzzle state", "error");
            }
        } catch (error) {
            Logger.error("Error moving puzzle to review stage:", error);
            handleSnackbar(error?.message || "Error moving puzzle to review stage", "error");
        }
    };

    const handleGenerateWordNetwork = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!crossTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }

            const result = await AdminCrossBoardService.generateWordNetwork(crossTpl.id);

            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network generated successfully", "success");
                // Refresh puzzle data
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to generate word network", "error");
            }
        } catch (error) {
            Logger.error("Error generating word network:", error);
            handleSnackbar("Error generating word network", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleRefreshWordNetwork = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!crossTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }

            const language = crossTpl?.language || 'english';
            const result = await AdminCrossBoardService.refreshWordNetwork(crossTpl.id, language);

            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network refreshed successfully", "success");
                // Refresh puzzle data
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to refresh word network", "error");
            }
        } catch (error) {
            Logger.error("Error refreshing word network:", error);
            handleSnackbar("Error refreshing word network", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleFetchWordScores = async () => {
        if (!filledCoordinates || filledCoordinates.length === 0) {
            handleSnackbar("No words found in puzzle", "warning");
            return;
        }

        try {
            setLoadingScores(true);
            if (!scoreDialogOpen) {
                setScoreDialogOpen(true);
            }

            // Extract words from filledCoordinates
            const words = filledCoordinates
                .map(coord => coord.text?.trim())
                .filter(word => word && word.length > 0);

            if (words.length === 0) {
                handleSnackbar("No words found in puzzle", "warning");
                setScoreDialogOpen(false);
                return;
            }

            // Build query string with multiple words parameters
            const queryParams = words.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const language = crossTpl?.language || 'english';
            const url = `/home/dic/${language}/multiple?validOnly=false&${queryParams}`;

            // Call API directly with full URL (no params object since we built the query string manually)
            const result = await ApiService.getData(url, null);

            if (result && Array.isArray(result)) {
                setWordScores(result);
            } else {
                handleSnackbar("Failed to fetch word scores", "error");
                setWordScores([]);
            }
        } catch (error) {
            Logger.error("Error fetching word scores:", error);
            handleSnackbar("Error fetching word scores", "error");
            setWordScores([]);
        } finally {
            setLoadingScores(false);
        }
    };

    const handleSaveTitle = (definition, word) => {
        if (!definition || !word) {
            return;
        }

        // Open dialog to edit definition before saving
        setEditingDefinition(definition);
        setEditingWord(word);
        setOpenWordRedefineDialog(true);
    };

    const handleWordRedefineSave = async (editedDefinition) => {
        try {
            await AdminCrossBoardService.setClue(editingWord, editedDefinition, crossTpl.id, false);
            handleSnackbar("Definition saved successfully", "success");
            setOpenWordRedefineDialog(false);
            setEditingDefinition("");
            setEditingWord("");
            // Refresh puzzle data
            setRefresh(prev => prev + 1);
        } catch (error) {
            Logger.error("Error saving definition:", error);
            handleSnackbar("Error saving definition", "error");
        }
    };

    const handleWordRedefineCancel = () => {
        setOpenWordRedefineDialog(false);
        setEditingDefinition("");
        setEditingWord("");
    };

    const hideMiddlePanel = ['READY_TO_REVIEW', 'REVIEWED', 'PUBLISHED'].includes(crossTpl?.state);
    const rightPanelSize = hideMiddlePanel ? 8 : 4;

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography sx={{ mt: 4 }}>Loading...</Typography>
            </Box>
        );
    }

    if (!crossTpl) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">Puzzle not found</Typography>
            </Box>
        );
    }

    const wordsInPuzzle = filledCoordinates
        .filter((c) => c.text && typeof c.text === "string")
        .map((c) => ({ text: c.text.trim(), isScattered: c.direction === "scattered", positions: c.positions || [] }))
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base" }));

    const handleWordsInPuzzleClick = (item) => {
        setSelectedWord({ word: item.text, text: item.text, positions: item.positions || [] });
        setSelectedAssociatedWord(null);
        setSelectedManualWord(null);
        setSelectedNetworkWord(null);
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={crossTpl}
                puzzleType="crossword"
            />

            <Grid container spacing={2} sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                {/* Left side - Puzzle Preview */}
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666', mb: 1 }}>
                        Puzzle Preview
                    </Typography>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: "#fafafa",
                        overflow: "hidden",
                        mb: hasWordNetwork ? 2 : 0
                    }}>
                        <Box>
                            {square.length > 0 && square[0] && square[0].length > 0 ? (
                                <Box>
                                    {/* Column indices header */}
                                    <Box sx={{ display: 'flex', gap: '1px', mb: 0.5 }}>
                                        {/* Empty space for row index column */}
                                        <Box sx={{ width: 24, height: 24 }} />
                                        {/* Column numbers */}
                                        {Array.from({ length: 12 }, (_, colIndex) => (
                                            <Box
                                                key={colIndex}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    border: '1px solid transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                {colIndex}
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* Grid rows with row indices */}
                                    {square.map((row, rowIndex) => (
                                        <Box key={rowIndex} sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                                            {/* Row index */}
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                {rowIndex}
                                            </Box>
                                            {/* Puzzle row */}
                                            <MiniPuzRow
                                                rowIndex={rowIndex}
                                                row={row}
                                                hiddenWord={(selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word || selectedAssociatedWord?.text) || ""}
                                                positions={(selectedWord?.positions || selectedManualWord?.positions || selectedAssociatedWord?.positions) || []}
                                                onCellClick={handleCellClick}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography color="text.secondary" sx={{ p: 2 }}>
                                    No puzzle grid data available
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {/* Action Buttons - same layout as grid puzzle */}
                    {crossTpl?.state && crossTpl.state !== '' && !['DISCARDED', 'DISABLED'].includes(crossTpl.state) && (() => {
                        const actionsDisabled = ['REVIEWED', 'PUBLISHED', 'DISCARDED', 'DISABLED'].includes(crossTpl?.state);
                        return (
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={<FactCheckIcon />} onClick={() => setValidationDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }} size="small">
                                            Validation
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={loadingScores ? <CircularProgress size={16} color="inherit" /> : <ScoreIcon />} onClick={handleFetchWordScores} disabled={loadingScores} sx={{ color: '#ed6c02', borderColor: '#ed6c02', '&:hover': { backgroundColor: '#ed6c02', color: 'white', borderColor: '#ed6c02' }, whiteSpace: 'nowrap' }} size="small">
                                            Score
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={<OpenInNewIcon />} onClick={() => setNetworkDialogOpen(true)} disabled={loadingApplyTitleHook} sx={{ whiteSpace: 'nowrap' }} size="small">
                                            Network
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={loadingApplyTitleHook ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />} onClick={handleApplyTitleHook} disabled={crossTpl?.state !== DRAFT || actionsDisabled || loadingApplyTitleHook} sx={{ color: '#9c27b0', borderColor: '#9c27b0', '&:hover': { backgroundColor: '#9c27b0', color: 'white', borderColor: '#9c27b0' }, whiteSpace: 'nowrap' }} size="small">
                                            Hook The Title
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={loadingRearrange ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />} onClick={handleRearrangeWords} disabled={crossTpl?.state !== DRAFT || actionsDisabled || loadingApplyTitleHook || loadingRearrange} sx={{ color: '#1976d2', borderColor: '#1976d2', '&:hover': { backgroundColor: '#1976d2', color: 'white', borderColor: '#1976d2' }, whiteSpace: 'nowrap' }} size="small">
                                            Rearrange
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Button variant="outlined" fullWidth startIcon={<MenuBookIcon />} onClick={() => setFillDialogOpen(true)} disabled={crossTpl?.state !== DRAFT || actionsDisabled || loadingApplyTitleHook} sx={{ color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { backgroundColor: '#2e7d32', color: 'white', borderColor: '#2e7d32' }, whiteSpace: 'nowrap' }} size="small">
                                            Fill
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <IconButton onClick={handleStateBack} disabled={crossTpl?.state === DRAFT || loadingApplyTitleHook || loadingDraft || loadingReadyToReview || loadingReviewed || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                    <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', minWidth: 0 }}>
                                        <Button variant="outlined" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleDraftClick} startIcon={loadingDraft ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />} disabled={crossTpl?.state !== READY_TO_REVIEW || loadingApplyTitleHook || loadingDraft}>
                                            Draft
                                        </Button>
                                        <Button variant="outlined" color="success" sx={{ minWidth: 120, whiteSpace: 'nowrap' }} size="small" onClick={handleReadyForReview} startIcon={<CheckCircleIcon />} disabled={(crossTpl?.state !== DRAFT && crossTpl?.state !== REVIEWED) || loadingApplyTitleHook}>
                                            Ready to Review
                                        </Button>
                                        <Button variant="outlined" color="primary" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleReviewedClick} startIcon={loadingReviewed || loadingBackToReviewed ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />} disabled={((crossTpl?.state !== READY_TO_REVIEW || !isManager) && crossTpl?.state !== PUBLISHED) || loadingApplyTitleHook || loadingReviewed || loadingBackToReviewed}>
                                            Reviewed
                                        </Button>
                                        {isManager && (
                                            <Button variant="outlined" color="secondary" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={() => setPublishDialogOpen(true)} startIcon={loadingPublish ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />} disabled={crossTpl?.state !== REVIEWED || loadingPublish}>
                                                Publish
                                            </Button>
                                        )}
                                        <Button variant="outlined" sx={{ minWidth: 90, whiteSpace: 'nowrap', color: '#d32f2f', borderColor: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f', color: 'white', borderColor: '#d32f2f' } }} size="small" onClick={() => { setWordToDelete(null); setDiscardDialogOpen(true); }} startIcon={<DeleteIcon />} disabled={actionsDisabled}>
                                            Discard
                                        </Button>
                                    </Box>
                                    <IconButton onClick={handleStateForward} disabled={(crossTpl?.state === PUBLISHED || crossTpl?.state === REVIEWED) || (crossTpl?.state === READY_TO_REVIEW && !isManager) || loadingApplyTitleHook || loadingDraft || loadingReadyToReview || loadingReviewed || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                        <ArrowForwardIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                {crossTpl?.state === "REVIEWED" && (
                                    <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <span>
                                            This puzzle is <strong>REVIEWED</strong>. It will be published on schedule.
                                        </span>
                                    </Alert>
                                )}
                            </Box>
                        );
                    })()}
                    {/* Puzzle Statistics */}
                    {square.length > 0 && (
                        <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                {puzzleStats.hasDuplicates ? (
                                    <span style={{ color: '#d32f2f' }}>✗ Duplicates found</span>
                                ) : (
                                    <span style={{ color: '#2e7d32' }}>✓ No duplicates</span>
                                )}
                                {', '}
                                {puzzleStats.filledCells} cells filled ({puzzleStats.percentage}% complete
                                {puzzleStats.thresholdMet ? (
                                    <span style={{ color: '#2e7d32' }}> - {puzzleStats.thresholdPercentage}% threshold met</span>
                                ) : (
                                    <span style={{ color: '#d32f2f' }}> - {puzzleStats.thresholdPercentage}% threshold not met ({puzzleStats.cellsNeeded} more cells needed)</span>
                                )}
                                )
                            </Typography>
                        </Box>
                    )}
                </Grid>

                {!hideMiddlePanel && (
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                                {modeRef.current === 'assoc' ? 'Association Search' :
                                    modeRef.current === 'ai' ? 'AI Associations' :
                                        'Manual Entry'}
                            </Typography>
                            <ToggleButtonGroup
                                value={modeRef.current}
                                exclusive
                                onChange={handleModeChange}
                                size="small"
                            >
                                <ToggleButton value="assoc">Assoc</ToggleButton>
                                <ToggleButton value="manual">Manual</ToggleButton>
                                <ToggleButton value="ai">AI</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {modeRef.current === 'assoc' ? (
                            <CrossCanAssoc
                                hwTpl={crossTpl}
                                square={square}
                                onWordAdd={handleWordAdd}
                                onShowSnackbar={handleSnackbar}
                                onWordSelect={handleWordSelect}
                                onRefresh={() => setRefresh(prev => prev + 1)}
                            />
                        ) : modeRef.current === 'ai' ? (
                            <CrossCandAI
                                onWordAdd={handleManualWordAdd}
                                onWordSelect={handleManualWordSelect}
                                selectedManualWord={selectedManualWord}
                                onShowSnackbar={handleSnackbar}
                                square={square}
                                crossTpl={crossTpl}
                            />
                        ) : (
                            <CrossCandManual
                                onWordAdd={handleManualWordAdd}
                                onCheckWordAlreadyPlaced={checkWordAlreadyPlaced}
                                onWordSelect={handleManualWordSelect}
                                selectedManualWord={selectedManualWord}
                                onShowSnackbar={handleSnackbar}
                                square={square}
                                crossTpl={crossTpl}
                            />
                        )}
                    </Grid>
                )}

                {/* Right side - Words in puzzle & Word meaning */}
                <Grid size={{ xs: 12, md: rightPanelSize }} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    {hideMiddlePanel && wordsInPuzzle.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>Words in puzzle</Typography>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={crossTpl?.state === REVIEWED || !(selectedWord?.text || selectedWord?.word || selectedManualWord?.word || selectedAssociatedWord?.word || selectedAssociatedWord?.text || selectedNetworkWord)}
                                    onClick={() => {
                                        const text = (selectedWord?.text || selectedWord?.word || selectedManualWord?.word || selectedAssociatedWord?.word || selectedAssociatedWord?.text || selectedNetworkWord) || "";
                                        if (text) {
                                            setWordToDelete(text);
                                            setDiscardDialogOpen(true);
                                        }
                                    }}
                                    aria-label="Delete selected word"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {wordsInPuzzle.map((item, idx) => {
                                    const displayedWord = (selectedWord?.text || selectedWord?.word || selectedAssociatedWord?.text || selectedAssociatedWord?.word || selectedManualWord?.word || selectedNetworkWord) || "";
                                    const isSelected = displayedWord.toLowerCase() === item.text.toLowerCase();
                                    return (
                                        <Chip
                                            key={idx}
                                            label={item.isScattered ? `"${item.text}" (hidden)` : item.text}
                                            size="small"
                                            variant={isSelected ? "filled" : (item.isScattered ? "filled" : "outlined")}
                                            color={isSelected ? "primary" : (item.isScattered ? "secondary" : "default")}
                                            onClick={() => handleWordsInPuzzleClick(item)}
                                            sx={{
                                                mb: 0.5,
                                                cursor: 'pointer',
                                                ...(isSelected && {
                                                    fontWeight: 700,
                                                    boxShadow: 2,
                                                    border: '2px solid',
                                                    borderColor: 'primary.main'
                                                })
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                            Word Meaning
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setOpenBannedWordsPopup(true)}
                            sx={{ color: 'error.main' }}
                            aria-label="View banned words"
                        >
                            <BlockIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {(selectedWord || selectedManualWord || selectedAssociatedWord || selectedNetworkWord) ? (
                            <WordMeaning
                                selectedWord={(selectedNetworkWord || selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word) || ""}
                                onSaveDefinition={handleSaveTitle}
                                gridTplId={crossTpl?.id}
                                language={crossTpl?.language}
                                fullHeight={true}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Select a word from the {modeRef.current === 'assoc' ? 'puzzle words' :
                                    modeRef.current === 'ai' ? 'AI associations' :
                                        'results'} or word network to see its meaning
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Snackbar */}
            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            {/* Word already placed dialog (Manual / AI Associations) */}
            <WordAlreadyPlacedDialog
                open={alreadyPlacedDialogOpen}
                onClose={() => setAlreadyPlacedDialogOpen(false)}
                word={alreadyPlacedWord}
            />

            {/* Discard Confirmation Dialog */}
            <ConfirmDialog
                open={discardDialogOpen}
                setOpen={(open) => {
                    setDiscardDialogOpen(open);
                    if (!open) {
                        setWordToDelete(null);
                    }
                }}
                title={wordToDelete ? "Delete Word" : "Discard Puzzle"}
                message={wordToDelete ? `Would you like to delete the word "${wordToDelete}"?` : "Would you like to discard this puzzle?"}
                targetName=""
                targetId=""
                callback={wordToDelete ? handleConfirmDeleteWord : handleDiscardPuzzle}
            />

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable Puzzle"
                message="Would you like to disable this puzzle? It will be withdrawn from publication."
                warningAlert="If the puzzle is set to 'DISABLED' now, the puzzle will be disappeared on all user's page 1 week later."
                targetName=""
                targetId=""
                callback={handleDisablePuzzle}
            />
            <ConfirmDialog
                open={publishDialogOpen}
                setOpen={setPublishDialogOpen}
                title="Publish Puzzle"
                message="Would you like to publish this puzzle now?"
                extraContent={
                    <Alert severity="info" sx={{ mt: 2 }}>
                        The system publishes puzzles by schedule automatically. If you want to publish this puzzle now, click Yes.
                    </Alert>
                }
                targetName=""
                targetId=""
                callback={confirmPublish}
            />
            <ConfirmDialog
                open={backToReviewedDialogOpen}
                setOpen={setBackToReviewedDialogOpen}
                title="Move back to Reviewed"
                message="Would you like to move this puzzle back to Reviewed state?"
                warningAlert={
                            <>
                                If this puzzle is played 1 time or more, it can&apos;t be moved back to &quot;REVIEWED&quot;. It could be &quot;DISABLED&quot; state, request it to Young.
                                <br />
                                <br />
                                If the puzzle is set to &quot;DISABLED&quot; now, the puzzle will be disappeared on all user&apos;s page 1 week later.
                            </>
                        }
                targetName=""
                targetId=""
                callback={confirmBackToReviewed}
            />
            <ConfirmDialog
                open={showTitleHookDialog}
                setOpen={setShowTitleHookDialog}
                title="Hook The Title"
                message="Would you like to change the title using the Bedrock?"
                callback={confirmApplyTitleHook}
            />
            <ConfirmDialog open={draftDialogOpen} setOpen={setDraftDialogOpen} title="Move back to Draft?" message="Change puzzle state to Draft? You can edit the puzzle again." callback={confirmDraft} />

            <ConfirmDialog open={readyToReviewDialogOpen} setOpen={setReadyToReviewDialogOpen} title="Move back to Ready to Review?" message="Change puzzle state from Reviewed to Ready to Review?" callback={confirmReadyToReview} />

            <ConfirmDialog open={reviewedDialogOpen} setOpen={setReviewedDialogOpen} title="Mark as Reviewed" message="Would you like to mark this puzzle as Reviewed?" targetName="" targetId="" callback={confirmReviewed} />

            <ValidationDialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                onResolve={(resolveType) => {
                    setValidationDialogOpen(false);
                    setResolveDialogType(resolveType);
                    setResolveDialogOpen(true);
                }}
                gridTpl={crossTpl}
            />

            {/* Puzzle Type Selection Dialog */}
            <VerifyDialog
                key={`crossword-dialog-${crossTpl?.id}`}
                open={puzzleTypeDialogOpen}
                onClose={() => setPuzzleTypeDialogOpen(false)}
                puzzleId={crossTpl?.id}
                requestPuzzle={AdminCrossBoardService.requestCrossTpl}
                defaultPuzzleType="CROSSWORD"
                allowedPuzzleTypes={["CROSSWORD"]}
                onPuzzleTypeSelected={handlePuzzleTypeSelected}
                onShowSnackbar={handleSnackbar}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!crossTpl?.id) return;
                    await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, null);
                    setRefresh((prev) => prev + 1);
                }}
            />

            <ResolvePuzzleDialog
                open={resolveDialogOpen}
                onClose={() => setResolveDialogOpen(false)}
                gridTpl={crossTpl}
                resolveType={resolveDialogType}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!crossTpl?.id) return;
                    await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, null);
                }}
                onRefresh={() => setRefresh((prev) => prev + 1)}
                onShowSnackbar={handleSnackbar}
            />

            {/* Banned Words Popup */}
            <BannedWordsPopup
                open={openBannedWordsPopup}
                onClose={() => setOpenBannedWordsPopup(false)}
                language={crossTpl?.language || "english"}
            />

            <AddBannedWordDialog
                open={openAddBannedWordDialog}
                onClose={() => { setOpenAddBannedWordDialog(false); setSelectedWordForBanned(""); }}
                onAdd={async (word, category, wordLanguage) => {
                    setLoadingAddBanned(true);
                    try {
                        await BannedService.addWord(category, word, "", wordLanguage);
                        handleSnackbar(`"${word}" added to banned words`, "success");
                        setOpenAddBannedWordDialog(false);
                        setSelectedWordForBanned("");
                    } catch (err) {
                        Logger.error("Error adding banned word:", err);
                        handleSnackbar(err?.message ?? "Failed to add banned word", "error");
                    } finally {
                        setLoadingAddBanned(false);
                    }
                }}
                selectedWord={selectedWordForBanned}
                language={crossTpl?.language ?? "english"}
                loading={loadingAddBanned}
            />

            {/* WordRedefine Dialog */}
            <WordRedefine
                open={openWordRedefineDialog}
                onClose={handleWordRedefineCancel}
                onSave={handleWordRedefineSave}
                word={editingWord}
                definition={editingDefinition}
            />

            {/* Word Network Dialog for Mobile */}
            <WordNetworkDialog
                open={networkDialogOpen}
                onClose={() => setNetworkDialogOpen(false)}
                hasWordNetwork={hasWordNetwork}
                wordNetworkData={wordNetworkData}
                onDeleteWord={handleDeleteWordFromNetwork}
                onNodeSelect={(word) => {
                    // Clear other selections when selecting from network
                    setSelectedWord(null);
                    setSelectedAssociatedWord(null);
                    setSelectedManualWord(null);
                    setSelectedNetworkWord(word);
                }}
                onRefresh={handleRefreshWordNetwork}
                onGenerateWordNetwork={handleGenerateWordNetwork}
            />

            {/* Word Score Dialog */}
            <WordScoreDialog
                open={scoreDialogOpen}
                onClose={() => setScoreDialogOpen(false)}
                wordScores={wordScores}
                loadingScores={loadingScores}
                language={crossTpl?.language || "english"}
                onRefresh={async () => {
                    // Close dialog first
                    setScoreDialogOpen(false);
                    // Small delay to ensure dialog closes
                    setTimeout(async () => {
                        // Refresh word scores and reopen dialog
                        setScoreDialogOpen(true);
                        await handleFetchWordScores();
                    }, 100);
                }}
            />
            <CrossFillDialog
                open={fillDialogOpen}
                onClose={() => setFillDialogOpen(false)}
                crossTplId={crossTpl?.id}
                puzzleTitle={crossTpl?.title}
                puzzleType="Crossword"
                tags={crossTpl?.tags ?? crossTpl?.template?.tags ?? []}
                language={crossTpl?.language ?? "english"}
                thresholdMet={puzzleStats?.thresholdMet}
                thresholdPercentage={puzzleStats?.thresholdPercentage}
                cellsNeeded={puzzleStats?.cellsNeeded}
                emptyCellCount={puzzleStats ? puzzleStats.totalCells - puzzleStats.filledCells : undefined}
                filledCoordinates={filledCoordinates}
                onPlaceWords={handlePlaceWords}
                onTab1PlaceWord={handleMatchKeywordAdd}
                onTab2Add={handleFillPatternAdd}
                onTab3Add={handleMatchKeywordAdd}
                adding={loadingFillPatternAdd || loadingMatchKeywordAdd}
                onReadyToReview={() => {
                    setFillDialogOpen(false);
                    handleReadyForReview();
                }}
                readyToReviewDisabled={crossTpl?.state !== DRAFT || loadingApplyTitleHook}
                readyToReviewLoading={false}
                onAddBannedWord={(word) => {
                    if (!word?.trim()) return;
                    setSelectedWordForBanned(word.trim());
                    setOpenAddBannedWordDialog(true);
                }}
            />
        </Box>
    );
}

